import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import { triggerHaptic } from "@/lib/haptics"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-semibold transition-all duration-200 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_8px_20px_-8px_rgba(33,79,216,0.7)] hover:bg-blue-700 hover:text-white hover:shadow-[0_12px_28px_-10px_rgba(33,79,216,0.8)] hover:-translate-y-px",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-rose-600 hover:text-white",
        outline:
          "border border-border bg-white/80 text-foreground shadow-sm hover:border-primary/40 hover:bg-primary/5 hover:text-primary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-slate-200 hover:text-foreground",
        soft:
          "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
        success:
          "bg-emerald-600 text-white shadow-[0_8px_20px_-8px_rgba(16,185,129,0.7)] hover:bg-emerald-700 hover:text-white",
        ghost: "text-muted-foreground hover:bg-secondary hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-11 rounded-xl px-6 text-[15px]",
        icon: "h-9 w-9 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function spawnRipple(e) {
  const el = e.currentTarget;
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const rect = el.getBoundingClientRect();
  const d = Math.max(rect.width, rect.height) * 2;
  const x = (e.clientX || rect.left + rect.width / 2) - rect.left - d / 2;
  const y = (e.clientY || rect.top + rect.height / 2) - rect.top - d / 2;
  const span = document.createElement("span");
  span.className = "btn-ripple";
  span.style.cssText = `width:${d}px;height:${d}px;left:${x}px;top:${y}px`;
  el.appendChild(span);
  setTimeout(() => span.remove(), 600);
}

const Button = React.forwardRef(({ className, variant, size, asChild = false, onClick, haptic = "tap", ripple = true, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const handleClick = (e) => {
    if (haptic) triggerHaptic(haptic);
    if (ripple && !asChild) spawnRipple(e);
    onClick?.(e);
  };
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }), !asChild && "relative overflow-hidden")}
      ref={ref}
      onClick={handleClick}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
